import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Card,
    Chip,
    CircularProgress,
    CssBaseline,
    Divider,
    ThemeProvider,
    Typography,
    Skeleton,
    Tooltip
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CompareArrows as CompareArrowsIcon,
    Link as LinkIcon,
    AccountTree as AccountTreeIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetRugAnalytics } from '../../hooks';

// --- SCORING LOGIC (Recency Independent Version) ---
/**
 * The R.I.S.K. (Interconnectivity, Source, Key-Wallets) Scoring System.
 * Analyzes token holder connections, focusing on network structure.
 * @param {Array<Object>} connections - The array of holder connection objects from the API.
 * @param {Function} t - Translation function.
 * @returns {Object} A detailed scoring object.
 */
function calculateTrustScore(connections, t) {
    const WEIGHTS = {
        interconnectivity: 0.50,
        sourcePattern: 0.40,
        keyWallet: 0.10,
    };

    if (!connections || connections.length === 0) {
        return {
            finalScore: 100, riskLevel: t('token.risk.low'), summary: t('token.risk.noConnectionData'), breakdown: {},
            connectionDetails: { hubs: [], satellites: [], distributionMap: [] },
        };
    }

    const allHolderIds = new Set(connections.map(c => c.userId));
    const connectionMap = new Map(connections.map(c => [c.userId, c]));
    const hubs = connections.filter(holder =>
        holder.transferPeers.some(peer => peer.sentCount > 0 && allHolderIds.has(peer.peerId))
    );
    const hubIds = hubs.map(h => h.userId);
    const peerLookup = (peers) => {
        const map = new Map();
        (peers || []).forEach((peer) => {
            map.set(peer.peerId, {
                sharedAddresses: peer.sharedAddresses || [],
                sharedAddressCount: peer.sharedAddressCount ?? peer.sharedAddresses?.length ?? 0,
            });
        });
        return map;
    };

    const distributionMap = hubs.flatMap(hub => {
        const hubDepositPeers = peerLookup(hub.depositPeers);
        const hubWithdrawalPeers = peerLookup(hub.withdrawalPeers);

        return hub.transferPeers
            .filter(peer => peer.sentCount > 0 && allHolderIds.has(peer.peerId))
            .map(peer => {
                const targetConnection = connectionMap.get(peer.peerId) || {};
                const targetDepositPeers = peerLookup(targetConnection.depositPeers);
                const targetWithdrawalPeers = peerLookup(targetConnection.withdrawalPeers);

                const sharedDepositSet = new Set([
                    ...(hubDepositPeers.get(peer.peerId)?.sharedAddresses || []),
                    ...(targetDepositPeers.get(hub.userId)?.sharedAddresses || []),
                ]);

                const sharedWithdrawalSet = new Set([
                    ...(hubWithdrawalPeers.get(peer.peerId)?.sharedAddresses || []),
                    ...(targetWithdrawalPeers.get(hub.userId)?.sharedAddresses || []),
                ]);

                const reasons = [];

                if (sharedDepositSet.size > 0) {
                    reasons.push({
                        type: 'sharedDeposit',
                        addresses: Array.from(sharedDepositSet),
                    });
                }

                if (sharedWithdrawalSet.size > 0) {
                    reasons.push({
                        type: 'sharedWithdrawal',
                        addresses: Array.from(sharedWithdrawalSet),
                    });
                }

                reasons.push({
                    type: 'directTransfer',
                    sentCount: peer.sentCount,
                    receivedCount: peer.receivedCount,
                });

                return {
                    from: hub.userId,
                    to: peer.peerId,
                    sentCount: peer.sentCount,
                    lastTransferAt: peer.lastTransferAt,
                    reasons,
                };
            });
    });
    const satelliteIds = [...new Set(distributionMap.map(link => link.to))];

    const getInterconnectivityScore = () => {
        const centralizationRatio = hubs.length / connections.length;
        if (hubs.length === 0) return { score: 95, level: t('token.risk.low'), reason: t('token.risk.noCentralHubs') };
        if (centralizationRatio <= 0.25) return { score: 15, level: t('token.risk.high'), reason: t('token.risk.highlyCentralized', { count: hubs.length }) };
        if (centralizationRatio <= 0.5) return { score: 50, level: t('token.risk.medium'), reason: t('token.risk.someCentralization') };
        return { score: 95, level: t('token.risk.low'), reason: t('token.risk.scatteredConnections') };
    };

    const getSourcePatternScore = () => {
        const internalTransfers = connections.flatMap(c => c.transferPeers.filter(p => allHolderIds.has(p.peerId)));
        if (internalTransfers.length === 0) return { score: 95, level: t('token.risk.low'), reason: t('token.risk.noTransfersBetweenHolders') };
        const oneWayTransfers = internalTransfers.filter(p => p.sentCount === 0 || p.receivedCount === 0).length;
        const oneWayRatio = oneWayTransfers / internalTransfers.length;
        if (oneWayRatio >= 0.8) return { score: 10, level: t('token.risk.high'), reason: t('token.risk.waterfallDistribution') };
        if (oneWayRatio >= 0.4) return { score: 50, level: t('token.risk.medium'), reason: t('token.risk.mixedTransfers') };
        return { score: 95, level: t('token.risk.low'), reason: t('token.risk.organicTransfers') };
    };

    const getKeyWalletScore = () => {
        const interactingWallets = connections.filter(c => c.depositPeers.length > 0 || c.withdrawalPeers.length > 0).map(c => c.userId);
        if (interactingWallets.length > 0) return { score: 0, level: t('token.risk.critical'), reason: t('token.risk.exchangeInteraction'), interactingWallets };
        return { score: 100, level: t('token.risk.low'), reason: t('token.risk.noExchangeActivity') };
    };

    const scores = {
        interconnectivity: getInterconnectivityScore(),
        sourcePattern: getSourcePatternScore(),
        keyWallet: getKeyWalletScore(),
    };

    const finalScore = (scores.interconnectivity.score * WEIGHTS.interconnectivity) + (scores.sourcePattern.score * WEIGHTS.sourcePattern) + (scores.keyWallet.score * WEIGHTS.keyWallet);

    let riskLevel, summary;
    if (finalScore <= 40) {
        riskLevel = t('token.risk.high');
        summary = t('token.risk.highRiskSummary');
    } else if (finalScore <= 70) {
        riskLevel = t('token.risk.medium');
        summary = t('token.risk.mediumRiskSummary');
    } else {
        riskLevel = t('token.risk.low');
        summary = t('token.risk.lowRiskSummary');
    }

    return {
        finalScore: Math.round(finalScore), riskLevel, summary, breakdown: scores,
        connectionDetails: { hubs: hubIds, satellites: satelliteIds, distributionMap },
    };
}


// --- REACT COMPONENT ---
const WalletRiskReport = ({ connectionsData }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { t } = useTranslation();
    const analysis = calculateTrustScore(connectionsData, t);

    const rankMap = new Map(connectionsData.map(c => [c.userId, c.rank]));

    const riskColorMap = {
        [t('token.risk.high')]: 'error',
        [t('token.risk.critical')]: 'error',
        [t('token.risk.medium')]: 'warning',
        [t('token.risk.low')]: 'success',
    };

    const riskIconMap = {
        interconnectivity: <LinkIcon />,
        sourcePattern: <AccountTreeIcon />,
        keyWallet: <WarningIcon />,
    };

    const { finalScore, riskLevel, summary, breakdown, connectionDetails } = analysis;
    const color = riskColorMap[riskLevel] || 'grey';

    const shortenId = (id) => {
        if (!id) return 'N/A';
        if (id.length <= 8) return id;
        return `${ id.substring(0, 4) }...${ id.substring(id.length - 4) }`;
    };

    const renderConnectionTooltip = (link) => {
        if (!link?.reasons || link.reasons.length === 0) {
            return t('token.risk.connectionReasons.default');
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {link.reasons.map((reason, idx) => {
                    if (reason.type === 'sharedDeposit') {
                        const addressesToShow = (reason.addresses || []).slice(0, 3);
                        const remaining = (reason.addresses || []).length - addressesToShow.length;

                        return (
                            <Box key={`reason-${ idx }`}>
                                <Typography variant="caption" fontWeight="bold">
                                    {t('token.risk.connectionReasons.sharedDeposit', { count: reason.addresses?.length || 0 })}
                                </Typography>
                                {addressesToShow.map(address => (
                                    <Typography key={address} variant="caption" sx={{ display: 'block' }} title={address}>
                                        {shortenId(address)}
                                    </Typography>
                                ))}
                                {remaining > 0 && (
                                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        {t('token.risk.connectionReasons.additionalAddresses', { count: remaining })}
                                    </Typography>
                                )}
                            </Box>
                        );
                    }

                    if (reason.type === 'sharedWithdrawal') {
                        const addressesToShow = (reason.addresses || []).slice(0, 3);
                        const remaining = (reason.addresses || []).length - addressesToShow.length;

                        return (
                            <Box key={`reason-${ idx }`}>
                                <Typography variant="caption" fontWeight="bold">
                                    {t('token.risk.connectionReasons.sharedWithdrawal', { count: reason.addresses?.length || 0 })}
                                </Typography>
                                {addressesToShow.map(address => (
                                    <Typography key={address} variant="caption" sx={{ display: 'block' }} title={address}>
                                        {shortenId(address)}
                                    </Typography>
                                ))}
                                {remaining > 0 && (
                                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        {t('token.risk.connectionReasons.additionalAddresses', { count: remaining })}
                                    </Typography>
                                )}
                            </Box>
                        );
                    }

                    if (reason.type === 'directTransfer') {
                        return (
                            <Typography key={`reason-${ idx }`} variant="caption">
                                {t('token.risk.connectionReasons.directTransfer', {
                                    sentCount: reason.sentCount ?? 0,
                                    receivedCount: reason.receivedCount ?? 0,
                                })}
                            </Typography>
                        );
                    }

                    return (
                        <Typography key={`reason-${ idx }`} variant="caption">
                            {t('token.risk.connectionReasons.default')}
                        </Typography>
                    );
                })}
            </Box>
        );
    };

    return (
        <Card sx={{ minWidth: 320, maxWidth: 600, borderRadius: 2, boxShadow: 3 }}>
            <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)} sx={{ '&.Mui-expanded': { margin: 0 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Box display="flex" alignItems="center" width="100%">
                        <Box sx={{ position: 'relative', display: 'inline-flex', mr: 2 }}>
                            <CircularProgress variant="determinate" value={finalScore} color={color} size={50} thickness={4} />
                            <Box
                                sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Typography variant="caption" component="div" color="text.secondary" fontWeight="bold">
                                    {`${ finalScore }`}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="h6" component="div">
                                {t('token.risk.centralizationRisk')}
                            </Typography>
                            <Chip label={riskLevel} color={color} size="small" sx={{ fontWeight: 'bold' }} />
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {summary}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        {t('token.risk.riskBreakdown')}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.entries(breakdown).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {riskIconMap[key]}
                                    <Typography variant="body2" textTransform="capitalize">{t(`token.risk.${ key }`)}</Typography>
                                </Box>
                                <Chip label={value.level} color={riskColorMap[value.level]} size="small" />
                            </Box>
                        ))}
                    </Box>

                    {connectionDetails.hubs.length > 0 && (
                        <Accordion sx={{ mt: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2" fontWeight="bold">{t('token.risk.viewConnectionMap')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" fontWeight="bold">{t('token.risk.transactionHubs')} ({connectionDetails.hubs.length})</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                                    {connectionDetails.hubs.map(hub => (
                                        <Chip
                                            key={hub}
                                            label={`(#${ rankMap.get(hub) }) ${ shortenId(hub) }`}
                                            title={hub}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ))}
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body2" fontWeight="bold">{t('token.risk.transactionFlow')}</Typography>
                                {connectionDetails.distributionMap.map((link, index) => (
                                    <Tooltip
                                        key={`${ link.from }-${ link.to }-${ index }`}
                                        title={renderConnectionTooltip(link)}
                                        arrow
                                        placement="top"
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, fontFamily: 'monospace', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`(#${ rankMap.get(link.from) }) ${ shortenId(link.from) }`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                title={link.from}
                                            />
                                            <CompareArrowsIcon fontSize="small" color="action" />
                                            <Chip
                                                label={`(#${ rankMap.get(link.to) }) ${ shortenId(link.to) }`}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                title={link.to}
                                            />
                                        </Box>
                                    </Tooltip>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Alert severity="info" sx={{ mt: 2 }}>
                        <AlertTitle>{t('token.risk.importantContext')}</AlertTitle>
                        {t('token.risk.contextDescription')}
                    </Alert>

                </AccordionDetails>
            </Accordion>
        </Card>
    );
};


// --- APP WRAPPER FOR DEMONSTRATION ---
export default function TokenRisk({ tokenId }) {
    const { t } = useTranslation();
    const { data, loading, error } = useGetRugAnalytics(tokenId);

    if (loading) {
        return <Skeleton variant="rectangular" width={300} height={200} />;
    }

    if (error) {
        return (
            <Alert severity="error">
                <AlertTitle>{t('common.error')}</AlertTitle>
                {t('token.risk.errorLoadingData')}
            </Alert>
        );
    }

    return (

        <WalletRiskReport connectionsData={data.connections} />

    );
}
